import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import YearSlider from '../components/YearSlider'
import RoundResult from '../components/RoundResult'
import LangSwitch from '../components/LangSwitch'
import PhotoViewer from '../components/PhotoViewer'
import { scoreRound, type RoundResult as RR } from '../../../worker/lib/scoring'
import { buildShareString, MAX_DAILY_SCORE } from '../lib/share'
import { useT } from '../i18n'
import { fmt } from '../lib/format'

type DailyPhoto = {
  id: string
  year: number
  year_min: number
  year_max: number
  era: string
  lat: number
  lng: number
  place_name_en: string
  place_name_ar: string
  place_name_historical: string | null
  caption_en: string
  caption_ar: string
  trivia_en: string | null
  trivia_ar: string | null
  attribution_text: string | null
  source_url: string
  source_archive: string
  image_url: string
}

type DailyResponse = { date: string; photos: DailyPhoto[]; reason?: string }

export default function Play() {
  const { t, locale } = useT()
  const [data, setData] = useState<DailyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [round, setRound] = useState(0)
  const [guesses, setGuesses] = useState<Array<{ year: number } | null>>([])
  const [results, setResults] = useState<RR[]>([])
  const [revealed, setRevealed] = useState(false)

  const [yearInput, setYearInput] = useState(1950)
  const [startedAt, setStartedAt] = useState(Date.now())

  useEffect(() => {
    fetch('/api/daily')
      .then((r) => r.json() as Promise<DailyResponse>)
      .then((d) => {
        setData(d)
        setGuesses(new Array(d.photos.length).fill(null))
        setStartedAt(Date.now())
      })
      .catch((e: unknown) => setError(String(e)))
  }, [])

  const photo = data?.photos[round]
  const finished = !!data && round >= data.photos.length
  const total = useMemo(() => results.reduce((s, r) => s + r.total, 0), [results])

  function submit() {
    if (!photo) return
    const guess = { year: yearInput }
    const result = scoreRound(guess, {
      year: photo.year,
      year_min: photo.year_min,
      year_max: photo.year_max,
    })
    setGuesses((g) => g.map((x, i) => (i === round ? guess : x)))
    setResults((r) => [...r, result])
    setRevealed(true)
  }

  function next() {
    if (!data) return
    if (round + 1 >= data.photos.length) {
      const allGuesses = guesses.map((g, i) =>
        g ?? (i === round ? { year: yearInput } : null),
      )
      void fetch('/api/play', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          puzzle_date: data.date,
          rounds: data.photos.map((p, i) => {
            const g = allGuesses[i]
            const r = results[i]
            if (!g || !r) return null
            return {
              photo_id: p.id,
              year_guess: g.year,
              score: r.total,
              ms_taken: i === round ? Date.now() - startedAt : null,
            }
          }).filter(Boolean),
        }),
      }).catch(() => {})
    }
    setRound((r) => r + 1)
    setRevealed(false)
    setYearInput(1950)
  }

  if (error) return <Centered><div className="text-red-700">Error: {error}</div></Centered>
  if (!data) return <Centered><div className="text-earth-700/70">{t('play.loading')}</div></Centered>

  if (data.photos.length === 0) {
    return (
      <Centered>
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold font-display text-earth-900">{t('game.title')}</h1>
          <p className="text-earth-700/80">{t('play.no_photos')}</p>
          <p className="text-xs text-earth-700/60">{data.reason}</p>
          <Link to="/curate" className="text-sm underline text-earth-700">{t('play.open_curator')}</Link>
        </div>
      </Centered>
    )
  }

  if (finished) return <FinalScreen date={data.date} results={results} />

  return (
    <main className="min-h-screen bg-cream-50 text-earth-900">
      <header className="border-b border-cream-200 bg-cream-50/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-baseline justify-between gap-2">
          <Link to="/" className="font-display font-bold text-xl tracking-tight text-earth-900">
            {t('game.title')}
          </Link>
          <div className="flex items-baseline gap-3 text-xs text-earth-700/80 tabular-nums">
            <span>{t('play.round_n_of', { n: fmt(round + 1, locale), total: fmt(data.photos.length, locale) })}</span>
            <span className="hidden sm:inline">· {data.date}</span>
            <span>· {t('play.total')}: <strong className="text-earth-900">{fmt(total, locale)}</strong></span>
            <LangSwitch />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        <PhotoViewer src={photo!.image_url} className="min-h-[260px] sm:min-h-[480px]" />

        <div className="space-y-4">
          {!revealed ? (
            <>
              <YearSlider value={yearInput} onChange={setYearInput} />
              <button
                onClick={submit}
                className="w-full bg-earth-800 text-cream-50 rounded py-3 font-semibold touch-manipulation hover:bg-sand-600 transition"
              >
                {t('play.submit')}
              </button>
            </>
          ) : (
            <RoundResult
              result={results[results.length - 1]!}
              answer={{
                year: photo!.year,
                year_min: photo!.year_min,
                year_max: photo!.year_max,
                place_name_en: photo!.place_name_en,
                place_name_ar: photo!.place_name_ar,
                place_name_historical: photo!.place_name_historical,
                caption_en: photo!.caption_en,
                caption_ar: photo!.caption_ar,
                attribution_text: photo!.attribution_text,
                source_url: photo!.source_url,
              }}
              guess={{ year: yearInput }}
              onNext={next}
              isLast={round + 1 >= data.photos.length}
            />
          )}
        </div>
      </div>
    </main>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-cream-50 flex items-center justify-center p-6">
      {children}
    </main>
  )
}

function FinalScreen({ date, results }: { date: string; results: RR[] }) {
  const { t, locale } = useT()
  const total = results.reduce((s, r) => s + r.total, 0)
  const share = buildShareString(date, results, locale)
  const [copied, setCopied] = useState(false)

  async function copyShare() {
    try {
      await navigator.clipboard.writeText(share)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <Centered>
      <div className="text-center space-y-6 max-w-md text-earth-900">
        <h1 className="text-3xl font-bold font-display">{t('play.final_done', { date })}</h1>
        <div className="text-5xl sm:text-6xl font-bold tabular-nums font-display text-sand-700">
          {fmt(total, locale)}
        </div>
        <div className="text-sm text-earth-700/70">
          {t('play.final_of_n', { max: fmt(MAX_DAILY_SCORE, locale) })}
        </div>

        <pre className="bg-cream-100/90 border border-cream-200 rounded p-4 text-sm font-mono whitespace-pre-wrap text-start text-earth-800" dir="ltr">
{share}
        </pre>

        <button
          onClick={copyShare}
          className="w-full bg-earth-800 text-cream-50 rounded py-3 font-semibold touch-manipulation hover:bg-sand-600 transition"
        >
          {copied ? t('play.copied') : t('play.copy_share')}
        </button>

        <p className="text-xs text-earth-700/60">{t('play.come_back')}</p>
        <Link to="/" className="text-sm underline text-earth-700">{t('play.back_home')}</Link>
      </div>
    </Centered>
  )
}
